import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  fetchSavedContent,
  deleteSavedContent,
  saveContent,
  deleteDiscoverSavedContent
} from '../api/savedcontentApi'

export const useSavedContent = (user_id, options = {}) => {
  return useQuery({
    queryKey: ['GET_SAVED_CONTENT', user_id],
    queryFn: async () => {
      if (!user_id) throw new Error('User ID is required')
      const resp = await fetchSavedContent(user_id)
      const data = resp?.data || resp
      return data?.saved_content || data || []
    },
    enabled: !!user_id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    placeholderData: Array(3).fill({ id: 'placeholder', name: 'Loading...' }),
    ...options
  })
}

export const useSaveContent = (user_id, options = {}) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (content_id) => {
      if (!user_id) throw new Error('User ID is required')
      if (!content_id) throw new Error('Content ID is required')
      return await saveContent(content_id, user_id)
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(['GET_SAVED_CONTENT'])
      options.onSuccess?.(data, variables)
    },
    onError: (error, variables) => {
      console.error('Error saving content:', error)
      options.onError?.(error, variables)
    },
    ...options
  })
}

export const useDeleteSavedContent = (user_id, options = {}) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (content_ids) => {
      if (!user_id) throw new Error('User ID is required')
      if (!content_ids || content_ids.length === 0)
        throw new Error('Content IDs are required')
      return await deleteSavedContent(content_ids, user_id)
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(['GET_SAVED_CONTENT'])
      queryClient.invalidateQueries(['GET_USER_DASHBOARD'])
      if (options.onSuccess) {
        options.onSuccess(data, variables)
      }
    },
    onError: (error, variables) => {
      console.error('Error deleting saved content:', error)
      if (options.onError) {
        options.onError(error, variables)
      }
    },
    ...options
  })
}

export const useDiscoveDeleteContent = (options = {}) => {
  return useMutation({
    mutationKey: ['DISCOVER_UNSAVE'],
    mutationFn: async (coupon_id) => {
      return await deleteDiscoverSavedContent([coupon_id])
    },
    ...options
  })
}
